import { styled } from "styled-components";

import { InfoIcon, PlusIcon, UserGroupIcon } from "@vericus/cadmus-icons";
import {
  Alert,
  AvatarIcon,
  Button,
  colors,
  Divider,
  Pill,
  Popover,
  Text,
  Tooltip,
} from "@vericus/cadmus-ui";

import {
  AssessmentFragment,
  GroupFragment,
  MessageFragment,
  MessageRequestType,
  UserFragment,
} from "@/generated/graphql";
import { useRootNavigate } from "@/routes";
import { formatDateFull } from "@/utils/datetime";

import { useGroupChangeRequest } from "../hooks/use-group-change-request";
import * as styles from "./group-card.css";
import * as homeStyles from "./home-page.css";

interface GroupCardProps {
  assessment: AssessmentFragment;
  finalised: boolean;
  userGroup: GroupFragment | null;
  changeRequested?: MessageFragment | null;
  workGroupId?: string;
}

export function GroupCard(props: GroupCardProps) {
  const { assessment, finalised } = props;

  const settings = assessment.settings;
  const selfEnrolled = settings?.groupSelfEnrollmentEnabled;
  const deadline = settings?.groupSelfEnrollmentDeadline;

  const isDeadlinePassed = deadline ? new Date(deadline) < new Date() : false;
  const isFinalised = settings?.groupFinalised || isDeadlinePassed || finalised;

  return (
    <div className={homeStyles.steps}>
      <div className={homeStyles.stepIcon[isFinalised ? "final" : "initial"]}>
        <UserGroupIcon label="group people" size={30} />
      </div>
      <div className={styles.groupCardRoot}>
        <div className={styles.groupCard}>
          <div>
            <Content {...props} />
            {selfEnrolled && !isFinalised && deadline && (
              <>
                <ContentDivider />
                <div className={styles.progress}>
                  <Pill color={colors.successBg}>
                    Group Formation In Progress
                  </Pill>
                  <Text kind="bodySm">
                    <strong> Due:</strong> {formatDateFull(new Date(deadline))}
                  </Text>
                </div>
              </>
            )}
          </div>

          {settings?.openDate && (
            <ButtonComponent
              {...props}
              selfEnrolled={selfEnrolled ?? false}
              isFinalised={isFinalised}
            />
          )}
        </div>
        <Divider />
        {props.userGroup && <GroupMembers userGroup={props.userGroup} />}
      </div>
    </div>
  );
}

interface ButtonComponentProps extends GroupCardProps {
  selfEnrolled: boolean;
  isFinalised: boolean;
}

function ButtonComponent(props: ButtonComponentProps) {
  const sendMessage = useGroupChangeRequest(props.assessment.id);
  const rootNavigate = useRootNavigate();

  // Handle a request to change group
  const onRequestChangeHandler = () => {
    if (!props.workGroupId) return;
    sendMessage(props.workGroupId, {
      targetUserId: null,
      messageType: MessageRequestType.GroupChange,
      expiresInDays: 3,
    });
  };
  const { userGroup, changeRequested, selfEnrolled, isFinalised } = props;

  if (selfEnrolled && !isFinalised) {
    const Icon = userGroup ? (
      <UserGroupIcon label="join a group" size={24} />
    ) : (
      <PlusIcon label="join a group" size={16} />
    );
    return (
      <Button
        kind="secondary"
        icon={Icon}
        onClick={() => rootNavigate(`/group`)}
      >
        {userGroup ? "Edit Group" : "Join a Group"}
      </Button>
    );
  }

  if (userGroup) {
    return (
      <div className={styles.groupButton}>
        <div className={styles.groupButtonContent}>
          <Button
            kind="outline"
            onClick={onRequestChangeHandler}
            disabled={!!changeRequested}
          >
            Request Group Change
          </Button>
          <Tooltip
            content={
              "You can request a group change once. Your teacher will be notified."
            }
          >
            <InfoIcon label="info" size={20} />
          </Tooltip>
        </div>
      </div>
    );
  }

  return;
}

function Content(props: GroupCardProps) {
  const { userGroup, assessment, changeRequested } = props;
  const selfEnrolled = assessment.settings?.groupSelfEnrollmentEnabled;

  const headerText =
    selfEnrolled && userGroup
      ? `You have joined: ${userGroup.name}`
      : selfEnrolled
        ? "Join a Group"
        : `You are a member of: ${userGroup?.name}`;
  const title = userGroup
    ? selfEnrolled
      ? "You’ll collaborate with your group to submit a shared response."
      : `You were assigned to this group by your teacher.`
    : selfEnrolled
      ? "Join or create a group to participate in this project. If you do not select a group by the deadline, you will be automatically assigned to one."
      : "";
  let alertText;
  let alertSeverity: "info" | "error" | "success" | undefined;

  if (changeRequested && changeRequested.expiresAt) {
    const isExpired = new Date(changeRequested.expiresAt) < new Date();
    if (changeRequested.resolvedAt) {
      alertText = "Your group has been changed by your teacher.";
      alertSeverity = "success";
    } else if (isExpired) {
      alertText = "Your group change request has auto-expired.";
      alertSeverity = "error";
    } else {
      alertText = "Your teacher has been notified.";
      alertSeverity = "info";
    }
  }

  return (
    <div className={styles.groupCardContent}>
      <Text kind="headingFour">{headerText}</Text>
      <Text kind="bodyLine">{title}</Text>
      {alertText && alertSeverity && (
        <Alert
          severity={alertSeverity}
          className={styles.groupFormationAlert}
          size="sm"
        >
          {alertText}
        </Alert>
      )}
    </div>
  );
}

interface GroupMembersProps {
  userGroup: GroupFragment;
}

// List of first 5 Group Members with overflow memembers in a popover.
function GroupMembers(props: GroupMembersProps) {
  const { userGroup } = props;

  const totalMembers = userGroup.members.length;
  const displayMembers = userGroup.members.slice(0, 6);
  const remainingMembers =
    totalMembers > 6 ? userGroup.members.slice(6) : undefined;

  return (
    <div className={styles.memberList}>
      <Text kind="label" as="div">
        {totalMembers} Members:
      </Text>
      {displayMembers.map((member) => (
        <div className={styles.memberItem} key={member.id}>
          <AvatarIcon
            name={member.name}
            size="md"
            shade={member.colourset ?? undefined}
          />
          <Text kind="bodySm" as="div">
            {member.name}
          </Text>
        </div>
      ))}
      {remainingMembers && (
        <RemainingMembersPopover
          remainingMembers={remainingMembers}
          groupName={userGroup?.name}
        />
      )}
    </div>
  );
}

function RemainingMembersPopover(props: {
  groupName: string;
  remainingMembers: UserFragment[];
}) {
  const { remainingMembers, groupName } = props;

  return (
    <Popover.Root modal={false}>
      <Popover.Trigger asChild>
        <button className={styles.remainingButton}>
          +{remainingMembers.length}
        </button>
      </Popover.Trigger>
      <Popover.Content>
        <Popover.Card width={500} style={{ height: 500 }}>
          <div className={styles.groupNameHeader}>
            <Text kind="headingSix">{groupName}</Text>
            <Text kind="bodySm">{remainingMembers.length} members</Text>
          </div>
          <Divider />
          <div className={styles.memberGrid}>
            {remainingMembers.map((member) => (
              <div key={member.id} className={styles.memberItem}>
                <AvatarIcon
                  name={member.name}
                  size="md"
                  shade={member.colourset ?? undefined}
                />
                <Text kind="bodySm" className={styles.memberName}>
                  {member.name}
                </Text>
              </div>
            ))}
          </div>
        </Popover.Card>
      </Popover.Content>
    </Popover.Root>
  );
}

const ContentDivider = styled(Divider)`
  margin: 10px 0;
  width: 60%;
`;
